import React, { useEffect } from 'react';
import { Autocomplete, Button, Grid, TextField } from '@mui/material';

import { FormattedMessage, useIntl } from 'react-intl';
import LanguageUtils from '../../../utils/LanguageUtils';
import ReportLine005Filters from '../../../interfaces/filters/reportLine005Filters';
import { useDispatch, useSelector } from 'react-redux';
import PageUtils from '../../../utils/pageUtils';
import { fetchCustomers, getCustomers, isLoadingCustomers, resetCustomers } from '../../../reducers/customerReducer';
import CustomerFilters from '../../../interfaces/output/filters';
import Customer from '../../../interfaces/output/customer';
import GenericDatePicker from '../../common/widgets/genericDatePicker';
import { fetchD365FeedbackStatuses, getD365FeedbackStatuses } from '../../../reducers/d365FeedbackStatusReducer';
import { fetchReconcileStatuses, getReconcileStatuses } from '../../../reducers/reconcileStatusReducer';
import { fetchCorrectedStatuses, getCorrectedStatuses } from '../../../reducers/correctedStatusReducer';
import { fetchYesNo, getYesNo } from '../../../reducers/yesNoReducer';
import { fetchInvoiceTypes, getInvoiceTypes } from '../../../reducers/invoiceTypeReducer';
import GenericAutocomplete from '../../common/widgets/genericAutocomplete';
import D365FeedbackStatus from '../../../interfaces/output/d365FeedbackStatus';
import ReconcileStatus from '../../../interfaces/output/reconcileStatus';
import CorrectedStatus from '../../../interfaces/output/correctedStatus';
import YesNo from '../../../interfaces/output/YesNo';
import InvoiceType from '../../../interfaces/output/invoiceType';
// import { DatePicker } from '@mui/lab';
interface IFilterProps {
    filters: ReportLine005Filters;
    onChange: any;
    applyFilters: any;
    setDefaultState: any;
}

const messages = {
    invoiceDateFrom: LanguageUtils.createMessage('from date'),
    invoiceDateUntil: LanguageUtils.createMessage('to date'),
    d365FeedbackStatus: LanguageUtils.createMessage('D365 Feedback Status'),
    reconcileStatus: LanguageUtils.createMessage('Reconciliation Status'),
    correctedStatus: LanguageUtils.createMessage('Corrected Status'),
    yesNo: LanguageUtils.createMessage('Need Handling'),
    invoiceType: LanguageUtils.createMessage('Invoice Type'),
    customer: LanguageUtils.createMessage('customer'),
    customerName: LanguageUtils.createMessage('Customer Name'),
    resetFilters: LanguageUtils.createMessage('Reset filters'),
    applyFilters: LanguageUtils.createMessage('Apply filters')
};

export default function ReportLine005FilterComponent(props: IFilterProps): JSX.Element {
    const { filters, onChange, applyFilters, setDefaultState } = props;

    const [inputValue, setInputValue] = React.useState('');
    const paging = PageUtils.getMaxPaging();
    const customers = useSelector(getCustomers).content;
    const d365FeedbackStatus = useSelector(getD365FeedbackStatuses).content;
    const reconcileStatus = useSelector(getReconcileStatuses).content;
    const correctedStatus = useSelector(getCorrectedStatuses).content;
    const yesNo = useSelector(getYesNo).content;
    const invoiceTypes = useSelector(getInvoiceTypes).content;
    const propsIsLoadingCustomers = useSelector(isLoadingCustomers);
    const [customerValue, setCustomerValue] = React.useState<any>(null);

    const customerFilters = {
        name: inputValue
    } as CustomerFilters;
    const dispatch = useDispatch();
    const { formatMessage } = useIntl();

    useEffect(() => {
        // dispatch(fetchProductArenaTypes({ paging }));
        dispatch(fetchD365FeedbackStatuses({ paging }));
        dispatch(fetchReconcileStatuses({ paging }));
        dispatch(fetchCorrectedStatuses({ paging }));
        dispatch(fetchYesNo({ paging }));
        dispatch(fetchInvoiceTypes({ paging }));
    }, [dispatch]);

    const onChangeCustomer = (attribute: string, value: any) => {

        onChange(attribute, value?.id);
        setCustomerValue(value);
    };

    return (
        <Grid container p={2} direction="column">
            <Grid item>
                <Grid container spacing={2} mt={1} alignItems="flex-end">
                    <Grid item xs>
                        <Autocomplete
                            value={customerValue}
                            onChange={(e, newValue: any) => {
                                onChangeCustomer('customerId', newValue);
                            }}
                            id="controllable-states-demo"
                            getOptionLabel={(option: Customer) => option.name}
                            filterSelectedOptions
                            inputValue={inputValue}
                            onInputChange={(event, newInputValue: any, reason) => {
                                setInputValue(newInputValue);
                                if(newInputValue.length > 2) {
                                    dispatch(resetCustomers());
                                    dispatch(fetchCustomers({
                                        paging,
                                        filters: {
                                            ...customerFilters,
                                            name: newInputValue
                                        },
                                        throttle: true
                                    }));
                                }
                                if(reason === 'clear' && event.type === 'click') {
                                    dispatch(resetCustomers());
                                }
                            }}
                            filterOptions={(x) => x}
                            options={customers}
                            loading={propsIsLoadingCustomers}
                            renderInput={(params) => <TextField {...params} label={formatMessage(messages.customer)} variant="standard" />}
                        />
                    </Grid>
                    <Grid item xs>
                        <GenericDatePicker
                            name="fromDepartureDate"
                            label={messages.invoiceDateFrom}
                            value={filters?.invoiceDateFrom}
                            onChange={onChange}
                            type="start"
                        />
                    </Grid>
                    <Grid item xs>
                        <GenericDatePicker
                            name="toDepartureDate"
                            label={messages.invoiceDateUntil}
                            value={filters?.invoiceDateUntil}
                            onChange={onChange}
                            type="end"
                        />
                    </Grid>
                    <Grid item xs>
                        <GenericAutocomplete<D365FeedbackStatus>
                            options={d365FeedbackStatus}
                            value={filters.d365FeedbackStatus}
                            onChange={(obj: D365FeedbackStatus | null) => onChange('d365FeedbackStatus', obj?.d365FeedbackStatus)}
                            placeholder={messages.d365FeedbackStatus}
                            compareFn={(o: D365FeedbackStatus) => o.d365FeedbackStatus === filters.d365FeedbackStatus}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Grid item>
                <Grid container spacing={2} mt={1} alignItems="flex-end">
                    <Grid item xs>
                        <GenericAutocomplete<ReconcileStatus>
                            options={reconcileStatus}
                            value={filters.reconcileStatus}
                            onChange={(obj: ReconcileStatus | null) => onChange('reconcileStatus', obj?.reconcileStatus)}
                            placeholder={messages.reconcileStatus}
                            compareFn={(o: ReconcileStatus) => o.reconcileStatus === filters.reconcileStatus}
                        />
                    </Grid>
                    <Grid item xs>
                        <GenericAutocomplete<CorrectedStatus>
                            options={correctedStatus}
                            value={filters.correctedStatus}
                            onChange={(obj: CorrectedStatus | null) => onChange('correctedStatus', obj?.correctedStatus)}
                            placeholder={messages.correctedStatus}
                            compareFn={(o: CorrectedStatus) => o.correctedStatus === filters.correctedStatus}
                        />
                    </Grid>
                    <Grid item xs>
                        <GenericAutocomplete<YesNo>
                            options={yesNo}
                            value={filters.yesNo}
                            onChange={(obj: YesNo | null) => onChange('yesNo', obj?.YesNoName)}
                            placeholder={messages.yesNo}
                            compareFn={(o: YesNo) => o.YesNoName === filters.yesNo}
                        />
                    </Grid>
                    <Grid item xs>
                        <GenericAutocomplete<InvoiceType>
                            options={invoiceTypes}
                            value={filters.invoiceType}
                            onChange={(obj: InvoiceType | null) => onChange('invoiceType', obj?.code)}
                            placeholder={messages.invoiceType}
                            compareFn={(o: InvoiceType) => o.code === filters.invoiceType}
                        />
                    </Grid>
                </Grid>
            </Grid >
            <Grid item>
                <Grid container spacing={2} mt={1} justifyContent="flex-end">
                    <Grid item>
                        <Button onClick={setDefaultState}><FormattedMessage {...messages.resetFilters} /></Button>
                    </Grid>
                    <Grid item>
                        <Button onClick={applyFilters}><FormattedMessage {...messages.applyFilters} /></Button>
                    </Grid>
                </Grid>
            </Grid>
        </Grid >
    );
}